import {Suspense, useRef, useState} from 'react'
import {ThreeElements, ThreeEvent, useFrame} from '@react-three/fiber'
import * as THREE from 'three'
import {Matrix4, Vector3} from 'three'
import ImagePlane from './ImagePlane'
import useStore, {AsinTileData, Phase} from 'src/store';
import {toast} from "react-toastify";
import {ErrorBoundary} from "react-error-boundary";
import {useGLTF} from "@react-three/drei";
import waddleAnimationUrl from './assets/tile-waddle.glb';
import {CHECKEDOUT} from "src/globalVariables/globalVariables";
import ColorHash from 'color-hash';
import {lerp} from "src/math/linear";
import {arrangeEarthClusterTiles} from "src/arrangement/fulfillmentZones";

interface Props {
  tileData: AsinTileData;
}

export type Parametric = (t: number) => Vector3;

const colorHash = new ColorHash({hue: {min: 120, max: 160}, saturation: [.7, .8, .9], lightness: [.2, .3, .4, .5, .6, .7, .8]});

function getColor(storeId: string) {
  return colorHash.hex(storeId);
}

function getImageUrl(asin: string) {
  // We treat this as a fallback because it silently fails with a black image sometimes.
  return `https://m.media-amazon.com/images/P/${asin}.01._SCLZZZZZZZ_SS300_.jpg`
}

const ENTRANCE_WAYPOINT_SECONDS = 3;
const INITIAL_POSITION = new Vector3(0, 400, 400);
const INITIAL_SCALE = new Vector3(20, 20, 20);
const FINAL_SCALE = new Vector3(5, 5, 5);
const PARAMETRIC_TOLERANCE = 1;
const PARAMETRIC_ACCELERATION = 0.1;

function AsinTile(props: Props & ThreeElements['group']) {
  const ref = useRef<THREE.Group>(null!)
  const [hovered, setHovered] = useState(false)
  const [phase, setPhase] = useState<Phase>('ENTRANCE');
  const storeState = useStore((state) => state);

  const tileData = props.tileData;
  const { storeId } = tileData;

  const rearrangeCluster = () => {
    arrangeEarthClusterTiles(storeState.asinTiles, tileData.animationData.cluster?.name ?? '');
  }

  // const randomColor = useMemo(() => THREE.MathUtils.randInt(0, 0xffffff), []);
  // const randomColorStr = '#' + randomColor.toString(16);
  let opacity = 1.0
  let color = getColor(storeId);

  //Changing opacity to represent "Ghost tiles" i.e. checked out tiles
  if (tileData.checkedOut == CHECKEDOUT) {
    opacity = 0.5;
  }

  useFrame((state, delta) => {
    const animationData = tileData.animationData;
    if (!animationData) {
      return;
    }

    let target = animationData.targetPosition;
    let targetScale = FINAL_SCALE;

    const now = new Date();

    if (phase === 'ENTRANCE' && animationData.debutTime.getTime() > now.getTime()) {
      return;
    }

    const isEntranceMode = now.getTime() - animationData.debutTime.getTime() < 1000 * ENTRANCE_WAYPOINT_SECONDS;
    if (isEntranceMode && animationData.transition?.entranceWaypoint) {
      target = animationData.transition.entranceWaypoint;
      if (phase != 'ENTRANCE') {
        setPhase('ENTRANCE');
        animationData.phase = 'ENTRANCE';
      }
    }

    if (animationData.parametricFunction && animationData.parametricTargetValue !== undefined && animationData.parametricCurrentValue != undefined) {
      const t = animationData.parametricCurrentValue;
      const parametricExpectedPosition = animationData.parametricFunction(t);
      const distanceFromTarget = ref.current.position.distanceTo(parametricExpectedPosition);
      if (distanceFromTarget < PARAMETRIC_TOLERANCE) {
        animationData.parametricCurrentValue = lerp(t, animationData.parametricTargetValue, PARAMETRIC_ACCELERATION);
        const newPosition = animationData.parametricFunction(animationData.parametricCurrentValue);
        ref.current.position.set(newPosition.x, newPosition.y, newPosition.z);
        if (phase != 'PARAMETRIC') {
          setPhase('PARAMETRIC');
          animationData.phase = 'PARAMETRIC';
          rearrangeCluster();
        }
        return;
      }
      if (!target) {
        animationData.parametricCurrentValue = animationData.parametricTargetValue;
        target = animationData.parametricFunction(animationData.parametricTargetValue);
        if (phase != 'FLY') {
          setPhase('FLY');
          animationData.phase = 'FLY';
          rearrangeCluster();
        }
      }
    }

    if (target) {
      const targetVec = new Vector3(target.x, target.y, target.z);
      const toTarget = targetVec.clone().sub(ref.current.position);
      const distSq = toTarget.lengthSq();
      if (distSq > 0.0001) {
        if (!isEntranceMode) {
          ref.current.scale.lerp(targetScale, 0.1);
        }
        ref.current.position.lerp(targetVec, 0.1);
        if (!animationData.transition) {
          const lookMatrix = new Matrix4().lookAt(new Vector3(0, 0, 0), toTarget.setZ(-10), new Vector3(0, 1, 0));
          ref.current.setRotationFromMatrix(lookMatrix);
        }
      }
    }
  })

  const asinUrl = getImageUrl(tileData.asin);

  const clickHandler = (e: ThreeEvent<MouseEvent>) => {
    // Only show a toast if this tile was in the foreground / was the first object that the click
    // ray touched.
    if (e.object === e.intersections[0].object) {
      toast(
        <div className="kat-row">
          <div className="kat-col-sm-4">
            <img src={asinUrl} width="100%" style={{ marginTop: '20px' }} />
          </div>
          <div className="kat-col-sm-8">
            <a href={'https://www.amazon.com/dp/' + tileData.asin} target="_blank">{tileData.asin}</a>
            <div>Store: {storeId}</div>
          </div>
        </div>
      );
      console.log('Data of clicked ASIN: ', tileData);
    } else {
      console.log(`Ignored click on ${tileData.asin} because it was occluded.`);
    }
  }

  return (
    <group
      {...tileData}
      scale={INITIAL_SCALE}
      position={INITIAL_POSITION}
      ref={ref}
    >
      {/*The name of this group matches tile-waddle.glb, which holds animations.*/}
      <group name="Tile">
        <mesh
          onClick={clickHandler}
          onPointerOver={(event) => setHovered(true)}
          onPointerOut={(event) => setHovered(false)}
        >
          <boxGeometry args={[1, 1, 0.15]} />
          <meshStandardMaterial color={color} transparent={true} opacity={opacity}/>
        </mesh>
        <Suspense>
          <ErrorBoundary
            fallback={<></>}
            onError={(e) => {console.log(`Error rendering url ${asinUrl} for asin ${tileData.asin}:` + e) }}>
            <ImagePlane
                url={asinUrl}
                position={[0, 0, 0.13]}
                scale={[0.9, 0.9, 0.9]}
                zoom={phase === 'ENTRANCE' ? 1.2 : 6}
                opacity={opacity}
                vignette={phase !== 'ENTRANCE'}
                fullBright={phase === 'ENTRANCE'}
            />
          </ErrorBoundary>
        </Suspense>
      </group>
    </group>
  )
}

useGLTF.preload(waddleAnimationUrl)

export default AsinTile;
